import { Link } from 'react-router-dom'
import {
  AtSign,
  Bell,
  Bot,
  Building2,
  ChevronRight,
  DollarSign,
  Filter,
  KeyRound,
  ListChecks,
  Package,
  Plug,
  Receipt,
  ShieldCheck,
  SlidersHorizontal,
  Store,
  Tags,
  UserCog,
  Wand2,
  type LucideIcon,
} from 'lucide-react'
import type { Permission } from '@b4l/shared'
import { useAuthStore } from '../../stores/auth'
import { AntetPagina } from '../../components/ui/AntetPagina'

export interface SetareLink {
  to: string
  label: string
  icon: LucideIcon
  permission: Permission
  /** O propozitie: ce schimbi acolo. */
  descriere: string
}

/**
 * Setarile, intr-o singura pagina cu sectiuni, in locul celor 20 de intrari din bara laterala
 * (schelet aprobat de Madalin, 12.10.2026).
 *
 * Aici stau si setarile integrarilor (Shopify, Meta, eMag, ANAF, ExpertAccounts), care pana
 * acum erau in grupul fiecarei integrari, dupa regula „o integrare se administreaza de unde se
 * foloseste". Regula s-a schimbat: in bara stau paginile de lucru, iar tot ce se configureaza o
 * data si apoi nu se mai atinge sta aici, intr-un singur loc.
 *
 * Bara laterala citeste aceeasi lista: randul „Setari" apare doar daca omul vede macar o
 * setare, iar cautarea din bara gaseste si paginile de aici.
 */
export const SECTIUNI_SETARI: { titlu: string; icon: LucideIcon; linkuri: SetareLink[] }[] = [
  {
    titlu: 'Firmă & acces',
    icon: KeyRound,
    linkuri: [
      {
        to: '/setari/firma',
        label: 'Profil firmă',
        icon: Building2,
        permission: 'settings.company:read',
        descriere: 'Datele firmei folosite în facturi, contracte și rapoarte.',
      },
      {
        to: '/setari/utilizatori',
        label: 'Utilizatori',
        icon: UserCog,
        permission: 'settings.users:read',
        descriere: 'Conturi, roluri, parole.',
      },
      {
        to: '/setari/roluri-acces',
        label: 'Roluri & acces',
        icon: ShieldCheck,
        permission: 'settings.access:manage',
        descriere: 'Ce pagini vede fiecare rol.',
      },
      {
        to: '/setari/angajati',
        label: 'Angajați',
        icon: UserCog,
        permission: 'hr.employees:read',
        descriere: 'Angajații și contul lor de avans (542), pentru deconturi.',
      },
    ],
  },
  {
    titlu: 'Catalog & nomenclatoare',
    icon: Tags,
    linkuri: [
      {
        to: '/setari/produse',
        label: 'Catalog produse',
        icon: Package,
        permission: 'settings.products:read',
        descriere: 'Produse, coduri EA, costuri și prețuri.',
      },
      {
        to: '/setari/canale-vanzare',
        label: 'Canale vânzare',
        icon: Store,
        permission: 'settings.sales_channels:read',
        descriere: 'Canalele din Listări, Matrice prețuri și Campanii promo.',
      },
      {
        to: '/setari/centre-cost',
        label: 'Centre de cost',
        icon: DollarSign,
        permission: 'settings.cost_centers:read',
        descriere: 'Centrele pe care se încadrează deconturile și cererile de print.',
      },
      {
        to: '/setari/categorii-cheltuieli',
        label: 'Categorii cheltuieli',
        icon: Receipt,
        permission: 'settings.expense_categories:read',
        descriere: 'Leagă cheltuielile din deconturi de conturile din EA.',
      },
      {
        to: '/setari/instrumente-promovare',
        label: 'Instrumente de promovare',
        icon: Wand2,
        permission: 'settings.promo_instruments:read',
        descriere: 'Ce se poate cumpăra la un partener: planogramă, casetă, vitrină.',
      },
    ],
  },
  {
    titlu: 'Reguli & notificări',
    icon: SlidersHorizontal,
    linkuri: [
      {
        to: '/setari/comenzi',
        label: 'Comenzi (prag + prețuri)',
        icon: ListChecks,
        permission: 'orders:approve',
        descriere: 'Discountul peste care o comandă cere aprobare, și prețurile.',
      },
      {
        to: '/setari/notificari',
        label: 'Notificări email',
        icon: Bell,
        permission: 'alerts:read',
        descriere: 'Rezumatul zilnic al alertelor: cine îl primește și când.',
      },
    ],
  },
  {
    titlu: 'Email & AI',
    icon: AtSign,
    linkuri: [
      {
        to: '/setari/email',
        label: 'SMTP / IMAP',
        icon: AtSign,
        permission: 'settings.email:read',
        descriere: 'Căsuțele din care CRM-ul trimite emailuri și citește mesajele.',
      },
      {
        to: '/setari/filtre',
        label: 'Filtre email',
        icon: Filter,
        permission: 'settings.filters:read',
        descriere: 'Ce mesaje ajung în CRM și unde, în ordinea priorității.',
      },
      {
        to: '/setari/ai',
        label: 'AI / Anthropic',
        icon: Bot,
        permission: 'settings.users:read',
        descriere: 'Cheia Anthropic pentru funcțiile AI ale CRM-ului și costul lor.',
      },
    ],
  },
  {
    titlu: 'Integrări',
    icon: Plug,
    linkuri: [
      {
        to: '/setari/shopify',
        label: 'Shopify — setări și sincronizare',
        icon: Plug,
        permission: 'marketing.shopify:read',
        descriere: 'Legătura cu magazinul și sincronizarea comenzilor.',
      },
      {
        to: '/setari/meta',
        label: 'Meta',
        icon: Plug,
        permission: 'marketing.meta:read',
        descriere: 'Tokenul din Business Manager pentru citirea cheltuielilor.',
      },
      {
        to: '/setari/emag',
        label: 'eMAG — setări și sincronizare',
        icon: Plug,
        permission: 'settings.emag:read',
        descriere: 'Contul de vânzător și sincronizarea.',
      },
      {
        to: '/setari/anaf',
        label: 'ANAF — token SPV',
        icon: Plug,
        permission: 'anaf:read',
        descriere: 'Conexiunea la SPV pentru eFactura.',
      },
      {
        to: '/setari/ea',
        label: 'ExpertAccounts',
        icon: Plug,
        permission: 'settings.ea:read',
        descriere: 'Conexiunea la baza de contabilitate.',
      },
    ],
  },
]

/** Sectiunile cu ce vede omul. O sectiune fara nicio setare permisa nu apare deloc. */
export function setariVizibile(permissions: readonly string[]) {
  return SECTIUNI_SETARI.map((s) => ({
    ...s,
    linkuri: s.linkuri.filter((l) => permissions.includes(l.permission)),
  })).filter((s) => s.linkuri.length > 0)
}

export function SetariPage() {
  const permissions = useAuthStore((s) => s.permissions)
  const sectiuni = setariVizibile(permissions)

  return (
    <div>
      <AntetPagina
        titlu="Setări"
        descriere="Tot ce se configurează o dată: firma, accesul, nomenclatoarele, integrările."
      />
      {sectiuni.length === 0 ? (
        <p className="text-corp text-b4l-text-muted">Rolul tău nu are nicio setare de modificat.</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {sectiuni.map((s) => (
            <section
              key={s.titlu}
              className="grid content-start gap-1 rounded-caseta border border-b4l-border bg-b4l-surface p-3"
            >
              <h2 className="mb-1 flex items-center gap-2 px-2 text-sectiune text-b4l-text">
                <s.icon size={16} className="text-b4l-blue" />
                {s.titlu}
              </h2>
              {s.linkuri.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="group flex items-start gap-3 rounded-caseta px-2 py-2 hover:bg-b4l-bg-soft"
                >
                  <l.icon size={16} className="mt-0.5 shrink-0 text-b4l-text-dim" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-corp font-medium text-b4l-text">{l.label}</span>
                    <span className="block text-mic text-b4l-text-muted">{l.descriere}</span>
                  </span>
                  <ChevronRight
                    size={16}
                    className="mt-0.5 shrink-0 text-b4l-text-dim group-hover:text-b4l-text"
                  />
                </Link>
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  )
}
